import { ActivityIndicator, Pressable, ScrollView } from "react-native";
import { useLocalSearchParams } from "expo-router";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Text, View } from "@src/components";
import { sizes } from "@src/constants/sizes";
// @ts-expect-error baseline 2026-02-16
import { ReportsSection, ReportsListItem } from "@src/components/web";
import { Chip } from "@visioni/a3s-ui/components";
import { isWeb } from "@src/utils/web-helpers";
import { useRoadSenseReportList } from "@src/features/roadsense/hooks/useRoadSenseReportList";
import { roadSenseSiteRows } from "@src/features/roadsense/site-list/site-rows";
import { resolvedLocalTimeZone } from "@src/utils/format-date";

/**
 * RoadSense **site list** — the landing screen at `…/reports/roadsense`.
 *
 * Lists every (lifecycle, site) pair (`roadSenseSiteRows`); most tenants have a
 * single lifecycle, so there is no lifecycle picker. RoadSense-owned screen, a
 * sibling of (not a fork of) the WasteSense picker.
 */
export default function RoadSenseSiteListScreen() {
  const { tenantId } = useLocalSearchParams<{ tenantId: string }>();
  const { theme } = useUnistyles();

  if (!isWeb) {
    return (
      <View flex={1} alignItems="center" justifyContent="center" padding="3xl" gap="md">
        <Text textStyle="sm/semibold" color="text" textAlign="center">
          RoadSense reports are only available on the web client.
        </Text>
        <Text textStyle="xs/regular" color="textMuted" textAlign="center">
          Open this page in your browser to view your reports.
        </Text>
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      <View gap="6xl">
        <View gap="sm" px="md">
          <Text textStyle="lg/semibold" fontSize={22} style={{ color: theme.colors.red[600] }}>
            RoadSense
          </Text>
          <Text textStyle="sm/regular" color="textMuted">
            A clear picture of vehicle flow and traffic insights.
          </Text>
          <Text textStyle="sm/regular" color="textMuted">
            Pick a site to see its traffic report.
          </Text>
        </View>

        <ReportsSection title="Sites">
          <SiteListBody tenantId={tenantId} />
        </ReportsSection>
      </View>
    </ScrollView>
  );
}

function SiteListBody({ tenantId }: { tenantId: string }) {
  const { reports, isLoading, isError, isTenantPending, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useRoadSenseReportList();

  if ((isLoading || isTenantPending) && reports.length === 0) {
    return (
      <View padding="3xl" alignItems="center" gap="md">
        <ActivityIndicator />
        <Text textStyle="xs/regular" color="textMuted">
          Loading reports…
        </Text>
      </View>
    );
  }

  if (isError) {
    return (
      <View padding="3xl" alignItems="center" gap="sm">
        <Text textStyle="sm/semibold" color="text">
          Couldn&apos;t load reports
        </Text>
        <Text textStyle="xs/regular" color="textMuted" textAlign="center">
          Something went wrong loading your reports. Please refresh the page to try again.
        </Text>
      </View>
    );
  }

  if (reports.length === 0) {
    return (
      <View padding="3xl" alignItems="center" gap="sm">
        <Text textStyle="sm/semibold" color="text">
          No sites yet
        </Text>
        <Text textStyle="xs/regular" color="textMuted" textAlign="center">
          Sites appear here once the first RoadSense report has been generated for your account.
        </Text>
      </View>
    );
  }

  return (
    <View gap="md">
      {roadSenseSiteRows(reports, new Date(), resolvedLocalTimeZone()).map((row) => {
        if (!row.clickable || row.siteId === undefined || row.siteName === undefined) {
          return <NonReadyReportRow key={row.key} title={row.periodLabel} status={row.placeholderStatus ?? "failed"} />;
        }
        return (
          <ReportsListItem
            key={row.key}
            title={row.siteName}
            href={`/tenants/${tenantId}/reports/roadsense/report/${encodeURIComponent(row.reportId)}/site/${encodeURIComponent(row.siteId)}`}
          >
            <View flexDirection="row" alignItems="center" gap="sm">
              <Text textStyle="xs/regular" color="textMuted">
                {row.periodLabel}
              </Text>
              {row.isOpen ?
                <Chip label="Live" variant="outlined" />
              : null}
            </View>
          </ReportsListItem>
        );
      })}
      {hasNextPage ?
        <Pressable
          onPress={isFetchingNextPage ? undefined : () => fetchNextPage()}
          accessibilityRole="button"
          accessibilityState={{ disabled: isFetchingNextPage }}
          style={styles.loadMore}
        >
          {isFetchingNextPage ?
            <View flexDirection="row" gap="sm" alignItems="center">
              <ActivityIndicator />
              <Text textStyle="xs/regular" color="textMuted">
                Loading older reports…
              </Text>
            </View>
          : <Text textStyle="xs/semibold" color="text">
              Load older reports
            </Text>
          }
        </Pressable>
      : null}
    </View>
  );
}

/**
 * Static (non-clickable) card for a lifecycle that has never finished
 * generating, so it has no sites to open yet. Mirrors the `ReportsListItem`
 * card chrome but without a link/chevron. Copy is split per status so users
 * can tell a report that's still processing apart from one that failed.
 */
function NonReadyReportRow({ title, status }: { title: string; status: "generating" | "failed" }) {
  const subLabel = status === "generating" ? "Being generated" : "Not currently available";
  return (
    <View style={styles.nonReadyCard}>
      <Text textStyle="sm/regular" color="textMuted">
        {title}
      </Text>
      <Text textStyle="xs/regular" color="textMuted">
        {subLabel}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingTop: 40,
    paddingBottom: 40,
    paddingHorizontal: 40,
    maxWidth: sizes.webContentMaxWidth,
    width: "100%",
  },
  loadMore: {
    paddingVertical: 12,
    alignItems: "center",
  },
  // Mirrors the `ReportsListItem` card chrome (surface/border/radius) so a
  // non-clickable row sits flush in the same list, minus the link + chevron.
  nonReadyCard: {
    paddingVertical: theme.spacing["3xl"],
    paddingHorizontal: theme.spacing.xl,
    backgroundColor: theme.colors.surfaceCard,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.md,
    minHeight: 56,
    gap: theme.spacing.md,
    justifyContent: "center",
  },
}));
